import { Modal as BootstrapModal, Accordion, AccordionContext } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Setting from "../../services/setting";
import { useCallback, useContext, useMemo, useState, useRef } from "react";
import { useEffect } from "react";
import Icon from "../controls/Icon";
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { noPendingChangesObject } from "../../config/settingConfig";
import SettingItem from "./Setting";
import SettingGroup from "./SettingGroup";
import ActionButton from "./ActionButton";
import CustomSettingType from "./CustomSettingType";
import CustomSettingTypeValue from "./CustomSettingTypeValue";
import ConfirmationPrompt from "../controls/ConfirmationPrompt";
import SettingsSetUtils from "../../utils/settingsSetUtils";
import PubSub from "../../services/PubSub";
import SearchBar from "../controls/SearchBar";

const SettingsView = ({
    show = true,
    showCancel = true,
    onCancel,
    settingsSetId,
    permissions,
    context = {}
}) => {
    const ref = useRef();
    const [translate] = useTranslation();
    const [settingsHeader, setSettingsHeader] = useState('OTM_SETTINGS');
    const [customTypesHeader, setCustomTypesHeader] = useState('OTM_CUSTOM_SETTING_TYPES');
    const [initialData, setInitialData] = useState({});
    const [pendingChanges, setPendingChanges] = useState(noPendingChangesObject());
    const [customSettingTypesObj, setCustomSettingTypesObj] = useState([]);
    const [customSettingTypes, setCustomSettingTypes] = useState([]);
    const [customTypesInUse, setCustomTypesInUse] = useState({});
    const [customValuesInUse, setCustomValuesInUse] = useState({});
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [searchPrompt, setSearchPrompt] = useState('');

    const [activeKeys, setActiveKeys] = useState([]);
    const [view, setView] = useState("Settings");
    const [isValidObj, setIsValidObj] = useState({});
    const [isValid, setIsValid] = useState(true);
    const [loading, setLoading] = useState(true);
    const [settingsPermissions, setSettingsPermissions] = useState({ create: false, read: false, update: false, delete: false });

    const resetSettings = useCallback(() => {
        setInitialData({});
        setCustomSettingTypesObj([]);
        setCustomSettingTypes([]);
        setCustomTypesInUse({});
        setCustomValuesInUse({});
    }, []);

    const getSettings = useCallback(async () => {
        setLoading(true);
        const result = await Setting.getSettings(settingsSetId, context.type, context.objectId);
        if (result.data) {
            setInitialData(result.data);

            setInitialData((currentInitialData) => {
                const newInitialData = { ...currentInitialData };

                newInitialData.settingsGroups?.sort((a, b) => a.sequence - b.sequence);
                newInitialData.settingsGroups?.forEach(settingGroup => settingGroup.settings?.sort((a, b) => a.sequence - b.sequence));
                newInitialData.customSettingTypes?.forEach(customSettingType => customSettingType.values?.sort((a, b) => a.sequence - b.sequence));

                return newInitialData;
            });

            setCustomSettingTypesObj(result.data.customSettingTypes);
            setCustomSettingTypes(result.data.customSettingTypes?.map(obj => obj.name));
        }
        else {
            resetSettings();
        }
        setLoading(false);
        setIsValidObj({});
        setPendingChanges(noPendingChangesObject());
    }, [settingsSetId, context.type, context.objectId, resetSettings]);

    useEffect(() => {
        if (permissions) {
            if (context.editorMode === true) {
                setSettingsPermissions({
                    create: permissions.create,
                    read: permissions.read,
                    update: true,
                    delete: permissions.delete
                });
            }
            else if (context.versionUsed === true) {
                setSettingsPermissions({
                    create: permissions.create,
                    read: permissions.read,
                    update: false,
                    delete: permissions.delete
                });
            }
            else {
                setSettingsPermissions(permissions);
            }
        }
        else {
            setSettingsPermissions({ create: false, read: false, update: false, delete: false });
        }

    }, [context.versionUsed, context.editorMode, permissions]);

    useEffect(() => {
        if (show && settingsSetId && context.objectId) {
            getSettings();
        }
        else {
            resetSettings();

            setIsValidObj({});
            setPendingChanges(noPendingChangesObject());
        }

        const id = PubSub.subscribe("refresh-settings", () => {
            if (show && settingsSetId && context.objectId) {
                getSettings();
            }
            else {
                resetSettings();

                setIsValidObj({});
                setPendingChanges(noPendingChangesObject());
            }
        });

        return () => {
            PubSub.unsubscribe("refresh-settings", id);
        };
    }, [show, settingsSetId, context.objectId, getSettings, resetSettings]);

    const validator = ({ _id, field, fieldValid }) => {
        if (fieldValid) {
            setIsValidObj(current => {
                const newObj = { ...current };

                if (newObj[_id] && newObj[_id][field]) {
                    delete newObj[_id][field];

                    if (Object.keys(newObj[_id]).length === 0) {
                        delete newObj[_id];
                    }
                }

                return newObj;
            });
        }
        else {
            setIsValidObj(current => (
                {
                    ...current,
                    [_id]: {
                        ...current[_id],
                        [field]: field
                    }
                }
            ));
        }
    };

    const accordionEnabled = useMemo(() => {
        if (!isValid) {
            return activeKeys.length === 0;
        }

        return true;
    }, [isValid, activeKeys]);

    const updateActiveKey = useCallback((ids) => {
        if (accordionEnabled) {
            setActiveKeys(ids);
        }
        else if (ids.length > activeKeys.length) {
            setActiveKeys(ids);
        }
    }, [accordionEnabled, activeKeys]);

    const setActiveKeyAfterDelay = useCallback((ids) => {
        const delay = new Promise(resolve => setTimeout(resolve, 1));

        delay.then(() => updateActiveKey(ids));
    }, [updateActiveKey]);

    const insert = useCallback(({ fieldType, settingsGroupIdentifier, customSettingTypeId, customSettingTypeName }) => {
        SettingsSetUtils.Insert({
            fieldType,
            settingsGroupIdentifier,
            customSettingTypeId,
            customSettingTypeName,
            setInitialData,
            setPendingChanges,
            setActiveKeyAfterDelay,
            activeKeys
        });
    }, [setActiveKeyAfterDelay, activeKeys]);

    const update = useCallback(({ fieldType, newValue, initialValue, changedField, _id, settingsGroupIdentifier, customSettingTypeId, parentValue, setterFunc, sequence, name, description, type }) => {
        SettingsSetUtils.Update({
            fieldType,
            newValue,
            initialValue,
            changedField,
            _id,
            settingsGroupIdentifier,
            customSettingTypeId,
            parentValue,
            setterFunc,
            sequence,
            name,
            description,
            type,
            initialGroups: initialData.settingsGroups,
            setPendingChanges
        });
    }, [initialData.settingsGroups]);

    const deleteItem = useCallback(({ fieldType, _id, groupingId }) => {
        SettingsSetUtils.Delete({
            fieldType,
            _id,
            groupingId,
            setInitialData,
            setPendingChanges,
            setCustomTypesInUse,
            setCustomValuesInUse,
            setIsValidObj
        });
    }, []);

    const handleResetValue = useCallback(({ _id, settingsGroupIdentifier }) => {
        SettingsSetUtils.HandleResetValue({ _id, settingsGroupIdentifier, initialGroups: initialData.settingsGroups, setPendingChanges })
    }, [initialData.settingsGroups]);

    const save = useCallback(async () => {
        const response = await Setting.putSettings(settingsSetId, pendingChanges, context.type, context.objectId);

        if (response.status > 301 || response.status < 200) {
            return;
        }

        setPendingChanges(noPendingChangesObject());

        getSettings();
    }, [getSettings, pendingChanges, settingsSetId, context.type, context.objectId]);

    useEffect(() => {
        setIsValid(!Object.keys(isValidObj).length)
    }, [isValidObj]);

    const hasUnsavedChanges = useMemo(() => {
        let isEmpty = true

        Object.keys(pendingChanges).forEach(operation => Object.keys(pendingChanges[operation]).forEach(fieldType => {
            if (pendingChanges[operation][fieldType].length !== 0) {
                isEmpty = false;
            }
        }))

        return !isEmpty;
    }, [pendingChanges]);

    const canSave = useMemo(() => {
        return hasUnsavedChanges && isValid;
    }, [isValid, hasUnsavedChanges]);

    useEffect(() => {
        if (hasUnsavedChanges) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [hasUnsavedChanges]);

    const renderActionButton = useCallback(({ actionType, fieldType, _id, groupingId, parentValue, setterFunc, valueToCopy }) => {
        return (
            <ActionButton
                actionType={actionType}
                fieldType={fieldType}
                _id={_id}
                groupingId={groupingId}
                parentValue={parentValue}
                setterFunc={setterFunc}
                handleResetValue={handleResetValue}
                deleteItem={deleteItem}
                translate={translate}
                valueToCopy={valueToCopy}
            />
        );
    }, [deleteItem, handleResetValue, translate]);

    const renderSettings = useCallback((settings, settingsGroupIdentifier) => {
        if (settings?.length > 0) {
            return settings.map((setting) => {
                const initialSetting = initialData.settingsGroups.find(settingGroup => settingGroup.identifier === settingsGroupIdentifier)?.settings.find(settingItem => settingItem._id === setting._id)

                return (
                    <SettingItem
                        key={setting._id}
                        _id={setting._id}
                        setting={setting}
                        settingsGroupIdentifier={settingsGroupIdentifier}
                        initialSetting={initialSetting}
                        validator={validator}
                        update={update}
                        renderActionButton={renderActionButton}
                        customSettingTypesObj={customSettingTypesObj}
                        customSettingTypes={customSettingTypes}
                        setCustomValuesInUse={setCustomValuesInUse}
                        setCustomTypesInUse={setCustomTypesInUse}
                        permissions={settingsPermissions}
                    />
                );
            });
        }
        else {
            return (
                <label className="d-block h6">{translate("OTM_NO_SETTINGS")}</label>
            );
        }
    }, [customSettingTypes, customSettingTypesObj, initialData.settingsGroups, settingsPermissions, translate, renderActionButton, update]);

    const CustomToggle = ({ children, eventKey }) => {
        const { activeEventKey } = useContext(AccordionContext);
        const decoratedOnClick = useAccordionButton(eventKey);

        const isCurrentEventKey = activeEventKey.includes(eventKey);

        return (
            <div className="row align-items-center setting-row">
                <div className="col-auto">
                    <button type="button"
                        disabled={!accordionEnabled}
                        id={eventKey}
                        onClick={decoratedOnClick}
                        className="btn btn-outline-primary border-0 p-0"
                    >
                        <Icon className="d-block" icon={isCurrentEventKey ? "RemoveCircle" : "AddCircleOutline"} />
                    </button>

                </div>
                {children}
            </div>
        );
    };

    const renderCreateButton = useCallback(({ labelName, fieldType, settingsGroupIdentifier, settingsGroupEditable, customSettingTypeId, customSettingTypeName, css }) => {
        return (
            <button onClick={() => { insert({ fieldType, customSettingTypeId, customSettingTypeName, settingsGroupIdentifier, settingsGroupEditable }); setSearchPrompt(""); ref.current.resetSearch(); }}
                type="button"
                className={`btn btn-outline-primary border-0 p-0 m-0 ${css}`}
                disabled={!isValid && (fieldType === "SettingGroup" || fieldType === "CustomSettingType")}
                title={labelName}>
                <Icon icon={fieldType === "Setting" || fieldType === "CustomSettingTypeValue" ? "Add" : "PlaylistAdd"} className="d-block">{labelName}</Icon>
            </button>
        );
    }, [insert, isValid]);

    const renderSettingsGroup = useCallback((settingGroup) => {
        validator({ _id: settingGroup.identifier, field: 'settings', fieldValid: settingGroup.settings.length > 0 });

        return (
            <CustomToggle eventKey={settingGroup.identifier}>
                <SettingGroup
                    _id={settingGroup.identifier}
                    settingGroup={settingGroup}
                    validator={validator}
                    update={update}
                    renderActionButton={renderActionButton}
                    permissions={settingsPermissions}
                />
            </CustomToggle>
        );
    }, [settingsPermissions, renderActionButton, update]);

    const renderSettingsGroups = useMemo(() => {
        const filteredSettingsGroups = [];
        initialData.settingsGroups?.forEach((settingGroup) => {
            const settings = settingGroup.settings.filter((setting) => {
                if (setting.value) {
                    return setting.value.toString().toLowerCase().includes(searchPrompt.toLowerCase()) || setting.name.toLowerCase().includes(searchPrompt.toLowerCase());
                }
                return setting.parentValue?.toString().toLowerCase().includes(searchPrompt.toLowerCase()) || setting.name.toLowerCase().includes(searchPrompt.toLowerCase());
            });
            if (settings.length > 0) {
                filteredSettingsGroups.push({ ...settingGroup, settings });
            }
        });
        return (
            <>
                {
                    filteredSettingsGroups.length > 0 ?
                        filteredSettingsGroups.map((settingGroup) => {
                            return <Accordion.Item eventKey={settingGroup.identifier} key={settingGroup.identifier} className="px-3 py-2">
                                {renderSettingsGroup(settingGroup)}
                                <Accordion.Body key={settingGroup.identifier} className="mt-3 p-0">
                                    {renderSettings(settingGroup.settings, settingGroup.identifier)}
                                    {settingsPermissions.update && renderCreateButton({ labelName: translate("OTM_ADD_SETTING"), fieldType: "Setting", settingsGroupEditable: settingGroup.editable, settingsGroupIdentifier: settingGroup.identifier, css: "mt-3" })}
                                </Accordion.Body>
                            </Accordion.Item>
                        }) : <label className="h6">{translate("OTM_NO_SETTING_GROUPS")}</label>
                }

            </>
        );
    }, [searchPrompt, initialData, settingsPermissions, renderCreateButton, renderSettings, renderSettingsGroup, translate]);

    const renderSettingsAccordion = useMemo(() => {
        return (
            <>
                <Accordion alwaysOpen activeKey={activeKeys} onSelect={eventKey => updateActiveKey(eventKey)}>
                    {renderSettingsGroups}
                </Accordion>
            </>

        )
    }, [activeKeys, renderSettingsGroups, updateActiveKey]);

    const renderCustomSettingTypeValues = useCallback((customSettingTypeValues, customSettingTypeId, customSettingTypeName) => {
        if (customSettingTypeValues?.length > 0) {
            return customSettingTypeValues.map((customSettingTypeValue) => {
                const initialTypeValue = initialData.customSettingTypes.find(customSettingType => customSettingType._id === customSettingTypeId)?.values.find(value => value._id === customSettingTypeValue._id)

                return (
                    <CustomSettingTypeValue
                        key={customSettingTypeValue._id}
                        _id={customSettingTypeValue._id}
                        customSettingTypeValue={customSettingTypeValue}
                        customSettingTypeId={customSettingTypeId}
                        initialTypeValue={initialTypeValue}
                        validator={validator}
                        update={update}
                        renderActionButton={renderActionButton}
                        customValuesInUse={customValuesInUse}
                        customSettingTypeName={customSettingTypeName}
                        permissions={settingsPermissions}
                    />
                );
            });
        }
        else {
            return (
                <label className="d-block h6">{translate("OTM_NO_CUSTOM_SETTING_TYPE_VALUES")}</label>
            );
        }
    }, [customValuesInUse, initialData.customSettingTypes, settingsPermissions, translate, renderActionButton, update]);

    const renderCustomSettingType = useCallback((customSettingType) => {
        return (
            <CustomToggle eventKey={customSettingType.name}>
                <CustomSettingType
                    _id={customSettingType._id}
                    customSettingType={customSettingType}
                    validator={validator}
                    update={update}
                    renderActionButton={renderActionButton}
                    customTypesInUse={customTypesInUse}
                    permissions={settingsPermissions}
                />
            </CustomToggle>
        );
    }, [customTypesInUse, settingsPermissions, renderActionButton, update]);

    const renderCustomSettingTypes = useMemo(() => {
        return (
            <>
                {
                    initialData.customSettingTypes?.length > 0 ?
                        initialData.customSettingTypes.map((customSettingType, index) => (
                            <Accordion.Item eventKey={customSettingType.name} key={index} className="px-3 py-2">
                                {renderCustomSettingType(customSettingType)}
                                <Accordion.Body key={customSettingType.name} className="mt-3 p-0">
                                    {renderCustomSettingTypeValues(customSettingType.values, customSettingType._id, customSettingType.name)}
                                    {settingsPermissions.update && renderCreateButton({ labelName: translate("OTM_ADD_CUSTOM_VALUE"), fieldType: "CustomSettingTypeValue", customSettingTypeId: customSettingType._id, customSettingTypeName: customSettingType.name, css: "mt-3" })}
                                </Accordion.Body>
                            </Accordion.Item>)) :
                        <label className="h6">{translate("OTM_NO_CUSTOM_SETTING_TYPES")}</label>
                }
            </>
        )
    }, [initialData, settingsPermissions.update, renderCreateButton, renderCustomSettingType, renderCustomSettingTypeValues, translate]);

    const renderCustomSettingTypeValuesAccordion = useMemo(() => {
        return (
            <Accordion alwaysOpen activeKey={activeKeys} onSelect={eventKey => updateActiveKey(eventKey)}>
                {renderCustomSettingTypes}
            </Accordion>
        )
    }, [activeKeys, renderCustomSettingTypes, updateActiveKey]);

    const searchBar = useMemo(() => {
        return (
            <div className="px-3 mt-4">
                <SearchBar
                    ref={ref}
                    initialValue={searchPrompt}
                    placeholder={translate("OTM_SEARCH_SETTINGS")}
                    onChange={(value) => setSearchPrompt(value)} />
            </div>
        )
    }, [searchPrompt, translate])

    const renderCustomSettingTypesModal = useMemo(() => {
        return (
            <>
                <BootstrapModal.Header className="h5 text-secondary border-accenture card-header">
                    {translate(customTypesHeader)}
                    <div>
                        <button type="button" title={translate("OTM_SAVE")} onClick={save} className={`btn btn-outline-primary border-0 p-0 m-0 ${canSave ? '' : 'd-none'}`}>
                            <Icon icon="Save" className="d-block" />
                        </button>
                        {settingsPermissions.update && renderCreateButton({ labelName: translate("OTM_ADD_CUSTOM_SETTING"), fieldType: "CustomSettingType", css: "m-1" })}
                        <button type="button" title={translate("OTM_BACK")} disabled={hasUnsavedChanges} className="btn btn-outline-primary border-0 p-0 m-1" onClick={() => setView("Settings")}>
                            <Icon icon="Filter2" className="d-block">{translate("OTM_BACK")}</Icon>
                        </button>
                    </div>
                </BootstrapModal.Header>
                <BootstrapModal.Body className="d-flex flex-column m-0 card-body" style={{ 'overflowX': 'hidden', 'overflowY': 'auto' }}>
                    {
                        loading ?
                            <div>
                                <div className="spinner-border spinner-border-sm text-secondary" />
                                <label className="ps-1 h5 text-secondary">
                                    {translate("OTM_LOADING_SETTINGS")}
                                </label>
                            </div> :
                            renderCustomSettingTypeValuesAccordion
                    }
                </BootstrapModal.Body>
            </>
        )
    }, [hasUnsavedChanges, canSave, customTypesHeader, settingsPermissions.update, loading, renderCreateButton, renderCustomSettingTypeValuesAccordion, save, translate]);

    const renderSettingsModal = useMemo(() => {
        return (
            <>
                <BootstrapModal.Header className="h5 text-secondary border-accenture m-0 card-header" >
                    {translate(settingsHeader)}
                    <div>
                        <button type="button" title={translate("OTM_SAVE")} variant="light" onClick={save} className={`btn btn-outline-primary border-0 m-1 p-0 ${canSave ? '' : 'd-none'}`}>
                            <Icon icon="Save" className="d-block">{translate('OTM_SAVE')}</Icon>
                        </button>
                        {settingsPermissions.update && renderCreateButton({ labelName: translate("OTM_ADD_SETTING_GROUP"), fieldType: "SettingGroup", css: "m-1" })}
                        <button type="button" title={translate("OTM_CUSTOM_SETTING_TYPES")} disabled={hasUnsavedChanges} className="btn btn-outline-primary border-0 p-0 m-1" onClick={() => setView("CustomSettingTypes")}>
                            <Icon icon="Filter1" className="d-block">{translate('OTM_CUSTOM_SETTING_TYPES')}</Icon>
                        </button>
                        {showCancel && <button type="button" title={translate("OTM_CLOSE")} onClick={hasUnsavedChanges ? () => setShowConfirmation(true) : onCancel} className="btn btn-outline-primary border-0 p-0 m-1">
                            <Icon icon="Close" className="d-block">{translate("OTM_CLOSE")}</Icon>
                        </button>}
                    </div>
                </BootstrapModal.Header>
                {searchBar}
                <BootstrapModal.Body className="d-flex flex-column m-0 card-body" style={{ 'overflowX': 'hidden', 'overflowY': 'auto' }}>
                    {
                        loading ?
                            <div>
                                <div className="spinner-border spinner-border-sm text-secondary" />
                                <label className="ps-1 h5 text-secondary">
                                    {translate("OTM_LOADING_SETTINGS")}
                                </label>
                            </div> :
                            renderSettingsAccordion
                    }
                </BootstrapModal.Body>
            </>
        );
    }, [searchBar, hasUnsavedChanges, canSave, showCancel, onCancel, renderSettingsAccordion, loading, renderCreateButton, settingsPermissions.update, save, settingsHeader, translate]);

    useEffect(() => {
        switch (context.type) {
            case 'domain':
                setSettingsHeader(`Domain Settings${context.headerText ? ` for ${context.headerText}` : ''}`);
                setCustomTypesHeader(`Custom Setting Types${context.headerText ? ` for ${context.headerText}` : ''}`);
                break;
            case 'productVersion':
                setSettingsHeader(`Product Version Settings${context.headerText ? ` for ${context.headerText}` : ''}`);
                setCustomTypesHeader(`Custom Setting Types${context.headerText ? ` for ${context.headerText}` : ''}`);
                break;
            case 'environment':
                setSettingsHeader(`Environment Settings${context.headerText ? ` for ${context.headerText}` : ''}`);
                setCustomTypesHeader(`Custom Setting Types${context.headerText ? ` for ${context.headerText}` : ''}`);
                break;
            case 'deployment':
                setSettingsHeader(`Deployment Settings${context.headerText ? ` for ${context.headerText}` : ''}`);
                setCustomTypesHeader(`Custom Setting Types${context.headerText ? ` for ${context.headerText}` : ''}`);
                break;
            case 'environmentTemplate':
                setSettingsHeader(`Environment Template Settings${context.headerText ? ` for ${context.headerText}` : ''}`);
                setCustomTypesHeader(`Custom Setting Types${context.headerText ? ` for ${context.headerText}` : ''}`);
                break;
            default:
                break;
        }
    }, [context]);

    const renderModals = useMemo(() => (
        <>
            {view === "Settings" ? renderSettingsModal : renderCustomSettingTypesModal}
        </>
    ), [renderSettingsModal, renderCustomSettingTypesModal, view]);

    return (
        <>
            <div className="d-flex flex-column overflow-hidden">
                {renderModals}
            </div>
            <ConfirmationPrompt show={showConfirmation} message={translate("OTM_UNSAVED_SETTINGS")}
                onConfirm={() => { onCancel(); setShowConfirmation(false); PubSub.publish("no-unsaved-changes"); }}
                onCancel={() => setShowConfirmation(false)} />
        </>
    );
};

export default SettingsView;